import {
  Csharp,
  Go,
  Java,
  Nodejs,
  Php,
  Python,
  Ruby,
} from '@squareup/dex-icons/dex/language';
import { useCodeLanguage } from '@squareup/dex-store-access-shared-main-selectors';
import { CodeExampleLanguage } from '@squareup/dex-types-oas';
import { NativeButton } from '@squareup/dex-ui';
import { Box, Paragraph10 } from '@squareup/dex-ui-shared-base';
import { codeLanguageDisplayMap } from '@squareup/dex-utils-oas';
import React, { FC, useCallback, useMemo, SVGProps } from 'react';

import styles from './code-language-icon-selector.module.css';

type IconProps = SVGProps<SVGSVGElement> & React.HTMLAttributes<HTMLElement>;
const codeLanguageIconMap = new Map<CodeExampleLanguage, React.FC<IconProps>>([
  ['java', Java],
  ['csharp', Csharp],
  ['php', Php],
  ['javascript', Nodejs],
  ['python', Python],
  ['ruby', Ruby],
  ['go', Go],
]);

const CodeLanguageIconSelector: FC = () => {
  const [codeLanguage, setCodeLanguage] = useCodeLanguage();

  const codeLanguages = useMemo(
    () =>
      (
        [
          'python',
          'ruby',
          'javascript',
          'php',
          'java',
          'csharp',
          'go',
        ] as CodeExampleLanguage[]
      ).map((lang) => ({
        name: codeLanguageDisplayMap.get(lang),
        value: lang,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        icon: codeLanguageIconMap.get(lang)!,
      })),
    []
  );

  const handleSelect = useCallback(
    (language: CodeExampleLanguage) => () => {
      if (language !== codeLanguage) {
        // Simulate loading state when a code language button is selected
        setCodeLanguage(language, true);
      }
    },
    [codeLanguage, setCodeLanguage]
  );

  return (
    <Box className={styles['code-lang-container']}>
      {codeLanguages.map(({ name, value, icon: Icon }) => (
        <NativeButton
          className={styles['code-lang-button']}
          key={value}
          onClick={handleSelect(value)}
          trackingId={`code-lang-${value}`}
          {...(codeLanguage === value && { 'data-selected': '' })}
        >
          <Box className={styles['code-lang-icon-container']}>
            <Icon className={styles['code-lang-icon']} />
          </Box>
          <Paragraph10>{name}</Paragraph10>
        </NativeButton>
      ))}
    </Box>
  );
};

export { CodeLanguageIconSelector, codeLanguageIconMap };
